import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useReadinessRefresh } from '../utils/readinessBus';

const API = process.env.REACT_APP_BACKEND_URL;

// Granular post-onboarding checklist → human label + deep link. The order
// here is the priority order shown to the owner (most-impact first). Each
// entry receives the raw `sections` payload from /api/onboarding/readiness
// and returns whether the item is complete + how to fix it.
const CHECKS = [
  {
    key: 'name',
    label: 'Add your restaurant name',
    route: '/onboarding/setup',
    pick: (s) => !!s?.profile?.fields?.name,
  },
  {
    key: 'phone',
    label: 'Add a phone number',
    route: '/onboarding/setup',
    pick: (s) => !!s?.profile?.fields?.phone,
  },
  {
    key: 'address',
    label: 'Add your address',
    route: '/onboarding/setup',
    pick: (s) => !!s?.profile?.fields?.address,
  },
  {
    key: 'cuisine_type',
    label: 'Pick a cuisine type',
    route: '/onboarding/setup',
    pick: (s) => !!s?.profile?.fields?.cuisine_type,
  },
  {
    key: 'logo',
    label: 'Upload your logo',
    route: '/onboarding/setup',
    pick: (s) => !!s?.logo?.ok,
  },
  {
    key: 'business_hours',
    label: 'Set your business hours',
    route: '/onboarding/setup',
    pick: (s) => !!s?.business_hours?.ok,
  },
  {
    key: 'categories',
    label: 'Create your first menu category',
    route: '/onboarding/setup',
    pick: (s) => !!s?.categories?.ok,
  },
  {
    key: 'menu',
    label: 'Add at least one menu item',
    route: '/onboarding/setup',
    pick: (s) => !!s?.menu?.ok,
  },
  {
    key: 'order_channels',
    label: 'Enable an order receiving channel',
    route: '/onboarding/setup',
    pick: (s) => !!s?.order_channels?.ok,
  },
  {
    key: 'fulfillment',
    label: 'Turn on Pickup or Delivery',
    route: '/onboarding/setup',
    pick: (s) => !!s?.fulfillment?.ok,
  },
  {
    key: 'payouts',
    label: 'Verify Stripe payouts',
    route: '/onboarding/stripe-connect',
    pick: (s) => !!s?.payouts?.ok,
  },
];

const DISMISS_KEY = 'nofee_setup_score_dismissed';

/**
 * SetupScoreCard — sleek 0..100 progress card shown on the Dashboard
 * Overview tab while the restaurant still has setup fields incomplete.
 *
 * Hides itself when:
 *   - readiness.progress_percent === 100  (everything done)
 *   - the owner explicitly clicks the ✕  (persisted via localStorage)
 *
 * Surfaces the top 3 incomplete items with direct CTAs so the owner can
 * close the gaps without hunting through Settings.
 */
const SetupScoreCard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(Boolean(localStorage.getItem(DISMISS_KEY)));
    } catch { /* ignore */ }
  }, []);

  // Stable fetcher so the readiness bus + visibility-change subscriptions
  // don't tear down and re-create the listener on every render.
  const fetchReadiness = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/api/onboarding/readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadiness(r.data);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  // Re-validate the checklist whenever an autosave / explicit save fires
  // (`notifyReadinessChanged()` dispatches the event) OR the owner
  // returns to the tab. Prevents the "Phone Number Required" stale-badge
  // bug after editing Settings.
  useReadinessRefresh(fetchReadiness);

  if (!readiness || dismissed) return null;
  const percent = readiness.progress_percent ?? 0;
  if (percent >= 100) return null;

  const sections = readiness.sections || {};
  const incomplete = CHECKS.filter((c) => !c.pick(sections));
  // Only show the card while at least one signal is missing. If percent < 100
  // but no granular check is unticked it means the missing piece is the
  // publish/go-live step itself — we surface that as a single CTA.
  const items = incomplete.length > 0
    ? incomplete.slice(0, 3)
    : [{ key: 'publish', label: 'Publish your restaurant', route: '/onboarding/setup' }];

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Progress dial — pure SVG so we don't import another lib. Radius 22 so
  // the stroke (4px) fits inside a 52×52 viewbox cleanly.
  const dialR = 22;
  const dialC = 2 * Math.PI * dialR;
  const dialFill = (percent / 100) * dialC;

  return (
    <div
      data-testid="dashboard-setup-score-card"
      className="mb-6 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.06] via-zinc-950 to-zinc-950 p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          {/* Progress dial */}
          <div className="relative w-[52px] h-[52px] flex-shrink-0" aria-hidden="true">
            <svg viewBox="0 0 52 52" className="w-full h-full -rotate-90">
              <circle cx="26" cy="26" r={dialR} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
              <circle
                cx="26" cy="26" r={dialR}
                fill="none"
                stroke="url(#setupScoreGradient)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${dialFill} ${dialC}`}
                style={{ transition: 'stroke-dasharray 600ms ease' }}
              />
              <defs>
                <linearGradient id="setupScoreGradient" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="text-[13px] font-bold text-white tabular-nums"
                data-testid="setup-score-percent"
              >
                {percent}%
              </span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300 font-bold flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Setup Score
            </div>
            <h3 className="text-[15.5px] sm:text-base font-bold text-white mt-1 leading-tight">
              Your setup is {percent}% complete
            </h3>
            <p className="text-[12.5px] text-white/55 mt-1 leading-relaxed">
              {incomplete.length > 0
                ? `${incomplete.length} quick win${incomplete.length > 1 ? 's' : ''} to boost conversion.`
                : 'You\'re ready to publish your restaurant.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="setup-score-dismiss"
          aria-label="Dismiss setup score"
          className="w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Quick wins list */}
      <ul className="mt-4 space-y-1.5" data-testid="setup-score-items">
        {items.map((it) => (
          <li key={it.key}>
            <button
              type="button"
              onClick={() => navigate(it.route)}
              data-testid={`setup-score-item-${it.key}`}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-emerald-400/40 hover:bg-emerald-500/[0.05] transition-colors text-left group"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <CheckCircle2 className="w-4 h-4 text-white/25 group-hover:text-emerald-300 transition-colors flex-shrink-0" />
                <span className="text-[13px] font-semibold text-white/85 group-hover:text-white truncate">
                  {it.label}
                </span>
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-white/40 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SetupScoreCard;
